/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

interface Violation {
  type?: string;
  loc?: (string | number)[];
  msg?: string;
  input?: unknown;
}

const tryParse = (st: string): unknown => {
  try {
    return JSON.parse(st);
  } catch {
    return null;
  }
};

const joinLoc = (loc?: Violation['loc']): string => {
  if (!Array.isArray(loc) || loc.length === 0) {
    return '';
  }
  const start =
    typeof loc[0] === 'string' && (loc[0] === 'body' || loc[0] === 'query')
      ? 1
      : 0;
  return loc.slice(start).join('.');
};

export function humanizeApiErrorMessage(err: unknown): string {
  // 1) Best effort to extract a message we can parse
  const raw =
    // eslint-disable-next-line no-nested-ternary
    err instanceof Error
      ? typeof err.message === 'string'
        ? err.message
        : String(err.message)
      : typeof err === 'string'
        ? err
        : '';

  const parsed =
    raw && (raw.startsWith('{') || raw.startsWith('[')) ? tryParse(raw) : null;

  // 3) If array of validation errors
  if (Array.isArray(parsed)) {
    const lines = parsed.map((vi: Violation) => {
      const path = joinLoc(vi.loc);
      return path ? `${path}: ${vi.msg ?? 'Invalid'}` : (vi.msg ?? 'Invalid');
    });
    return lines.slice(0, 5).join('; ');
  }

  // 4) If parsed is an object with common fields
  if (parsed && typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>;
    if (typeof obj.detail === 'string' && typeof obj.title === 'string') {
      return `${obj.title}: ${obj.detail}`;
    }
    if (typeof obj.message === 'string') {
      return obj.message;
    }
    if (Array.isArray(obj.errors)) {
      return obj.errors

        .map((e: Error) =>
          typeof e === 'string' ? e : (e?.message ?? JSON.stringify(e)),
        )
        .join('; ');
    }
  }

  // 5) If message is [object Object]
  if (raw.includes('[object Object]')) {
    return 'Validation failed — check required fields.';
  }

  return raw || 'Unknown error occurred';
}
